/** The test state machine + timing engine.
 *
 * A run is a short wave: for each round, one lookup is sent to each of our
 * nameserver locations in quick succession (STAGGER apart), rounds ROUND_GAP
 * apart. Each probe is pinned to a location by its region subzone. The visual
 * gets a `fired` timeline (region + time) and a running clock `now`.
 *
 *   • sim  — same wave, fake resolvers/latencies (design scenarios)
 *   • live — POST /api/start, real probes, resolvers + per-location counts over SSE
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Connection, Nameserver, Phase, ResolverRow, Verdict } from "./types";
import { SCENARIOS, SIM_NAMESERVERS, SIM_PINGS, scenarioVerdict, type ScenarioKey } from "./scenarios";
import { startTest, openStream, fetchConnection, flagFor } from "./api";
import { fireProbe, type ProbeOutcome } from "./probe";

export type Mode = "sim" | "live";

export interface Fired { region: string; at: number; idx: number }

export interface LeakTest {
  phase: Phase;
  stopped: boolean;
  /** ms since the run started (rAF clock). */
  now: number;
  fired: Fired[];
  lit: number;
  servers: ResolverRow[];
  found: number;
  verdict: Verdict;
  vw: number;
  count: number;
  /** Expected run length in ms (for the "Run Time" label). */
  runMs: number;
  connection: Connection | null;
  reducedMotion: boolean;
  probeFailed: number;
  nameservers: Nameserver[];
  pings: Record<string, number | null>;
  nsCounts: Record<string, number>;
  run: () => void;
  stop: () => void;
}

const ROUNDS = 4;
const STAGGER = 230;   // ms between locations within a round
const ROUND_GAP = 1400; // ms between rounds
const GRACE = 2500;    // ms after the last probe to let answers arrive

function plan(ns: Nameserver[]): Fired[] {
  const out: Fired[] = [];
  let idx = 0;
  for (let r = 0; r < ROUNDS; r++) ns.forEach((n, i) => out.push({ region: n.id, at: r * ROUND_GAP + i * STAGGER, idx: idx++ }));
  return out;
}
function runLength(ns: Nameserver[]): number {
  const p = plan(ns);
  return (p[p.length - 1]?.at ?? 0) + GRACE;
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Time round trips to each nameserver's /ping: a warm-up, then the median of three. */
async function measurePings(ns: Nameserver[], report: (id: string, ms: number | null) => void): Promise<void> {
  await Promise.all(
    ns.map(async (n) => {
      if (!n.ping) return report(n.id, null);
      const once = async (): Promise<number | null> => {
        const t0 = performance.now();
        try {
          await fetch(`${n.ping}?t=${t0.toFixed(0)}${Math.random().toString(36).slice(2)}`, { cache: "no-store", mode: "cors", signal: typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(4000) : undefined });
          return performance.now() - t0;
        } catch {
          return null;
        }
      };
      await once();
      const samples: number[] = [];
      for (let i = 0; i < 3; i++) { const v = await once(); if (v != null) samples.push(v); }
      if (!samples.length) return report(n.id, null);
      samples.sort((a, b) => a - b);
      report(n.id, Math.round(samples[Math.floor(samples.length / 2)]!));
    }),
  );
}

function localVerdict(servers: ResolverRow[], isExit: boolean): Verdict {
  if (servers.length === 0) return "no-resolver";
  if (isExit && servers.every((s) => s.relation === "rapidtunnel")) return "secure";
  return servers.every((s) => s.relation !== "other") ? "consistent" : "external";
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function useLeakTest(mode: Mode, scenarioKey: ScenarioKey): LeakTest {
  const [phase, setPhase] = useState<Phase>("idle");
  const [stopped, setStopped] = useState(false);
  const [now, setNow] = useState(0);
  const [fired, setFired] = useState<Fired[]>([]);
  const [servers, setServers] = useState<ResolverRow[]>([]);
  const [verdict, setVerdict] = useState<Verdict>("no-resolver");
  const [vw, setVw] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  const [connection, setConnection] = useState<Connection | null>(null);
  const [probeFailed, setProbeFailed] = useState(0);
  const [nameservers, setNameservers] = useState<Nameserver[]>(mode === "sim" ? SIM_NAMESERVERS : []);
  const [pings, setPings] = useState<Record<string, number | null>>({});
  const [nsCounts, setNsCounts] = useState<Record<string, number>>({});

  const rm = useRef(prefersReducedMotion());
  const raf = useRef(0);
  const endTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runToken = useRef(0);
  const closeStream = useRef<(() => void) | null>(null);
  const isExitRef = useRef(false);
  const serversRef = useRef<ResolverRow[]>([]);
  const outcomes = useRef<ProbeOutcome[]>([]);
  const scenario = SCENARIOS[scenarioKey];

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (mode === "sim") {
      setConnection({
        ip: scenario.ip, ptr: scenario.rdns, asn: scenario.asn, org: scenario.operator,
        city: scenario.city, region: scenario.region, country: scenario.country, cc: scenario.cc,
        flag: scenario.flag, postal: scenario.postal, lat: scenario.lat, lon: scenario.lon,
        isRapidTunnelExit: scenario.isExit,
      });
    } else {
      fetchConnection().then((c) => { setConnection(c); isExitRef.current = !!c.isRapidTunnelExit; }).catch(() => setConnection(null));
    }
  }, [mode, scenarioKey, scenario]);

  const cancel = useCallback(() => {
    runToken.current++;
    cancelAnimationFrame(raf.current);
    if (endTimer.current) { clearTimeout(endTimer.current); endTimer.current = null; }
    closeStream.current?.();
    closeStream.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);

  const addServer = useCallback((row: ResolverRow) => {
    setServers((s) => {
      if (s.some((x) => x.ip === row.ip)) return s;
      const next = [...s, row].sort((a, b) => a.at - b.at);
      serversRef.current = next;
      return next;
    });
  }, []);

  /** rAF drives the animation clock; a plain timer ends the run, so a test
   *  still completes if the tab is in the background (rAF pauses there). */
  const startClock = useCallback((token: number, endAt: number, onEnd: () => void) => {
    const t0 = performance.now();
    const loop = (t: number) => {
      if (runToken.current !== token) return;
      const ms = t - t0;
      setNow(Math.min(ms, endAt));
      if (ms >= endAt) return;
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    endTimer.current = setTimeout(() => { if (runToken.current === token) { setNow(endAt); onEnd(); } }, endAt);
  }, []);

  const finish = useCallback((wasStopped: boolean) => {
    cancelAnimationFrame(raf.current);
    if (endTimer.current) { clearTimeout(endTimer.current); endTimer.current = null; }
    closeStream.current?.();
    closeStream.current = null;
    setStopped(wasStopped);
    setPhase("done");
    if (mode === "live") setVerdict(localVerdict(serversRef.current, isExitRef.current));
  }, [mode]);

  const begin = useCallback((ns: Nameserver[]) => {
    cancel();
    const token = runToken.current;
    setPhase("running"); setStopped(false); setNow(0); setFired([]); setServers([]); serversRef.current = [];
    setNsCounts({}); setProbeFailed(0); outcomes.current = [];
    return { token, schedule: plan(ns), endAt: runLength(ns) };
  }, [cancel]);

  const runSim = useCallback(() => {
    const { token, schedule, endAt } = begin(SIM_NAMESERVERS);
    setNameservers(SIM_NAMESERVERS); setPings({});
    setVerdict(scenarioVerdict[scenarioKey]);
    SIM_NAMESERVERS.forEach((n, i) => setTimeout(() => runToken.current === token && setPings((p) => ({ ...p, [n.id]: SIM_PINGS[n.id] ?? null })), 600 + i * 350));
    const all: ResolverRow[] = scenario.servers.map((s) => ({ ...s }));
    void (async () => {
      const t0 = performance.now();
      for (const f of schedule) {
        await sleep(Math.max(0, f.at - (performance.now() - t0)));
        if (runToken.current !== token) return;
        setFired((x) => [...x, f]);
        setTimeout(() => runToken.current === token && setNsCounts((c) => ({ ...c, [f.region]: (c[f.region] ?? 0) + 1 })), 520);
        all.filter((s) => s.at === f.idx + 1).forEach(addServer);
      }
    })();
    startClock(token, endAt, () => { all.forEach(addServer); finish(false); });
  }, [begin, scenario, scenarioKey, addServer, startClock, finish]);

  const runLive = useCallback(async () => {
    cancel();
    const token = runToken.current;
    setPhase("running"); setStopped(false); setNow(0); setFired([]); setServers([]); serversRef.current = []; setNsCounts({}); setProbeFailed(0);
    let resp;
    try { resp = await startTest(); } catch { setPhase("idle"); return; }
    if (runToken.current !== token) return;
    const ns = resp.nameservers ?? [];
    const { schedule, endAt } = { schedule: plan(ns), endAt: runLength(ns) };
    setNameservers(ns);
    setPings(Object.fromEntries(ns.map((n) => [n.id, null])));
    void measurePings(ns, (id, ms) => runToken.current === token && setPings((p) => ({ ...p, [id]: ms })));

    closeStream.current = openStream(resp.testId, {
      onNs: (counts) => runToken.current === token && setNsCounts(counts),
      onResolver: (row) => runToken.current === token && addServer(row),
      onDone: (v, resolvers, counts) => {
        if (runToken.current !== token) return;
        if (counts) setNsCounts(counts);
        resolvers.forEach(addServer);
        setVerdict(v);
        finish(false);
      },
    });

    void (async () => {
      const t0 = performance.now();
      for (const f of schedule) {
        await sleep(Math.max(0, f.at - (performance.now() - t0)));
        if (runToken.current !== token) return;
        setFired((x) => [...x, f]);
        void fireProbe(f.idx, resp.testId, resp.probeDomain, f.region).then((out) => {
          outcomes.current.push(out);
          if (out === "error") setProbeFailed((n) => n + 1);
        });
      }
      const o = outcomes.current;
      setTimeout(() => console.info(`[dnsleak] test ${resp.testId}: probes sent=${o.filter((x) => x === "sent").length} timeout=${o.filter((x) => x === "timeout").length} not-issued=${o.filter((x) => x === "error").length}`), GRACE);
    })();
    startClock(token, endAt, () => finish(false));
  }, [cancel, addServer, startClock, finish]);

  const run = useCallback(() => {
    if (phase === "running") return;
    if (mode === "sim") runSim();
    else void runLive();
  }, [phase, mode, runSim, runLive]);

  const stop = useCallback(() => {
    if (phase !== "running") return;
    runToken.current++;
    finish(true);
  }, [phase, finish]);

  const count = (mode === "sim" ? SIM_NAMESERVERS.length : Math.max(nameservers.length, 1)) * ROUNDS;

  return {
    phase, stopped, now, fired, lit: fired.length, servers, found: servers.length, verdict, vw, count,
    runMs: runLength(mode === "sim" ? SIM_NAMESERVERS : nameservers.length ? nameservers : SIM_NAMESERVERS),
    connection, reducedMotion: rm.current, probeFailed, nameservers, pings, nsCounts, run, stop,
  };
}

export { flagFor };
